/**
 * ClaimShield Operations - Internal Ops Entry Point
 *
 * Serves: ops.claimshieldpro.com
 * Audience: ClaimShield staff (Gavin, Dugger, consultants)
 * Build cache-bust: 2026-05-09T01:50 (poisoned text/html cache on /assets/index-Cl3NsNEc.js after dashboard widget drawer deploy)
 */
// Cache-bust marker — kept runtime-visible so Vite includes it in the entry chunk.
// Bumping this string forces a new bundle hash and escapes poisoned CF edge cache.
const __OPS_BUILD_TAG__ = '2026-05-09T01:50.dashboard-drawer-cache-bust'
if (typeof window !== 'undefined') (window as any).__OPS_BUILD_TAG__ = __OPS_BUILD_TAG__

import { createApp } from 'vue'
import { createPinia } from 'pinia'

// PrimeVue
import PrimeVue from 'primevue/config'
import { ClaimShieldAura } from './theme/claimshield-aura'
import ToastService from 'primevue/toastservice'
import ConfirmationService from 'primevue/confirmationservice'
import Tooltip from 'primevue/tooltip'

// PrimeVue components
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import Password from 'primevue/password'
import Card from 'primevue/card'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Dialog from 'primevue/dialog'
import Toast from 'primevue/toast'
import Menu from 'primevue/menu'
import Avatar from 'primevue/avatar'
import Badge from 'primevue/badge'
import Tag from 'primevue/tag'
import ProgressSpinner from 'primevue/progressspinner'
import Skeleton from 'primevue/skeleton'
import Select from 'primevue/select'
import Checkbox from 'primevue/checkbox'
import IconField from 'primevue/iconfield'
import InputIcon from 'primevue/inputicon'
import Message from 'primevue/message'
import TabView from 'primevue/tabview'
import TabPanel from 'primevue/tabpanel'
import TabMenu from 'primevue/tabmenu'
import Dropdown from 'primevue/dropdown'
import Sidebar from 'primevue/sidebar'
import Drawer from 'primevue/drawer'
import ConfirmDialog from 'primevue/confirmdialog'
import Panel from 'primevue/panel'
import Popover from 'primevue/popover'
import MultiSelect from 'primevue/multiselect'
import Textarea from 'primevue/textarea'
import DatePicker from 'primevue/datepicker'
import Paginator from 'primevue/paginator'
import AutoComplete from 'primevue/autocomplete'
import SelectButton from 'primevue/selectbutton'
import RadioButton from 'primevue/radiobutton'
import Chips from 'primevue/chips'
import ToggleSwitch from 'primevue/toggleswitch'
import InputNumber from 'primevue/inputnumber'
import FileUpload from 'primevue/fileupload'
import Timeline from 'primevue/timeline'

// App and router
import App from './App.vue'
import { router } from './router'
import { trackingPlugin, installErrorCapture } from '@claimshield/shared'
import { posthogPlugin } from '@claimshield/shared/plugins/posthog'
import { installErrorReporter } from './lib/errorReporter'
import { installOpsQuery } from './lib/query/opsQuery'

// Styles
import '@claimshield/shared/styles'
import 'primeicons/primeicons.css'
import './assets/primevue-overrides.css' // MUST be last to override Aura theme

// Build tag — bumping this string changes the entry-chunk hash, the escape
// hatch when a CDN edge poisons an /assets/index-*.js URL (2026-07-08 ×2).
;(window as any).__CS_OPS_BUILD = '2026-07-08-cachefix2'

// Create Vue app
const app = createApp(App)

// Pinia state management
const pinia = createPinia()
app.use(pinia)
// After pinia: the query wrapper reads the auth store for the user id in every key.
installOpsQuery(app)

// PrimeVue with Aura theme - customized for light backgrounds
app.use(PrimeVue, {
  theme: {
    preset: ClaimShieldAura,
    options: {
      prefix: 'p',
      darkModeSelector: '.dark-mode',
      cssLayer: {
        name: 'primevue',
        order: 'tailwind-base, primevue, tailwind-utilities',
      },
    },
  },
  ripple: true,
  pt: {
    // Global passthrough options to force light backgrounds
    dropdown: {
      root: {
        style: {
          background: '#f8fafc',
          backgroundColor: '#f8fafc',
          border: '2px solid #cbd5e1',
          color: '#1e293b'
        }
      },
      input: {
        style: {
          background: 'transparent',
          backgroundColor: 'transparent',
          color: '#1e293b'
        }
      },
      trigger: {
        style: {
          background: 'transparent',
          backgroundColor: 'transparent',
          color: '#1e293b'
        }
      },
      panel: {
        style: {
          background: 'white',
          backgroundColor: 'white',
          border: '2px solid #cbd5e1'
        }
      },
      item: {
        style: {
          background: 'white',
          backgroundColor: 'white',
          color: '#1e293b'
        }
      }
    },
    // Box only. A root style here painted a second 2px outline around the
    // box, so every ops checkbox rendered as a box inside a box (2026-10-23).
    checkbox: {
      box: {
        style: {
          background: 'white',
          backgroundColor: 'white',
          border: '2px solid #cbd5e1'
        }
      }
    }
  }
})

// PrimeVue services
app.use(ToastService)
app.use(ConfirmationService)

// PrimeVue directive
app.directive('tooltip', Tooltip)

// Register PrimeVue components globally
app.component('PButton', Button)
app.component('PInputText', InputText)
app.component('PPassword', Password)
app.component('PCard', Card)
app.component('PDataTable', DataTable)
app.component('PColumn', Column)
app.component('PDialog', Dialog)
app.component('PToast', Toast)
app.component('PMenu', Menu)
app.component('PAvatar', Avatar)
app.component('PBadge', Badge)
app.component('PTag', Tag)
app.component('PProgressSpinner', ProgressSpinner)
app.component('PSkeleton', Skeleton)
app.component('PSelect', Select)
app.component('PCheckbox', Checkbox)
app.component('PIconField', IconField)
app.component('PInputIcon', InputIcon)
app.component('PMessage', Message)
app.component('PTabView', TabView)
app.component('PTabPanel', TabPanel)
app.component('PTabMenu', TabMenu)
app.component('PDropdown', Dropdown)
app.component('PSidebar', Sidebar)
app.component('PDrawer', Drawer)
app.component('PConfirmDialog', ConfirmDialog)
app.component('PPanel', Panel)
app.component('PPopover', Popover)
app.component('PMultiSelect', MultiSelect)
app.component('PTextarea', Textarea)
app.component('PDatePicker', DatePicker)
app.component('PPaginator', Paginator)
app.component('PAutoComplete', AutoComplete)
app.component('PCalendar', DatePicker)  // PrimeVue 4 renamed Calendar to DatePicker
app.component('PSelectButton', SelectButton)
app.component('PRadioButton', RadioButton)
app.component('PChips', Chips)
app.component('PToggleSwitch', ToggleSwitch)
app.component('PInputNumber', InputNumber)
app.component('PFileUpload', FileUpload)
// Registered 2026-09-07: DriverHistory.vue had used <PTimeline> since 2026-03-19 with no registration, so its timeline never drew.
app.component('PTimeline', Timeline)

// Router
app.use(router)

// Tracking (invisible analytics — page views, time-on-page)
// The `app` label is explicit on both call sites, not defaulted on one of
// them — so the pair reads as a pair and nobody has to remember which app
// owns the default.
app.use(trackingPlugin, { router, app: 'ops' })

// Product analytics (knowledge/product-analytics/TRACK.md). Runs BESIDE the
// in-house tracker above, not instead of it — they answer different questions
// while the trial runs (§7.1). Inert unless the hostname is allowlisted in
// @claimshield/shared/plugins/posthog.ts, and the SDK itself is behind a
// dynamic import, so a non-allowlisted build downloads nothing.
//
// Session replay is OFF here and that is not a default someone picked: this app
// renders SsnReveal.vue on two views, so a replay of an ops session would
// record a consultant revealing a full SSN outside the audit_logs trail that
// governs every other reveal of that number (§7.2).
app.use(posthogPlugin, {
  router,
  app: 'ops',
  redact: [
    { pattern: /\/insurance\/submissions\/[^/?#&\s]+/g, replacement: '/insurance/submissions/:token' },
    { pattern: /\/insurance\/(?!submissions(?:\/|[?#&]|$))[^/?#&\s]+/g, replacement: '/insurance/:token' },
    { pattern: /(\/(?:sign|onboard|voe-verify|quote)\/)[^/?#&\s]+/g, replacement: '$1:token' },
    { pattern: /%2[fF]insurance%2[fF]submissions%2[fF](?:(?!%2[fF]|%3[fF]|%26|%23)[^&#?\s])+/g, replacement: '%2Finsurance%2Fsubmissions%2F:token' },
    { pattern: /%2[fF]insurance%2[fF](?!submissions(?:%2[fF]|%3[fF]|%26|%23|[&#?]|$))(?:(?!%2[fF]|%3[fF]|%26|%23)[^&#?\s])+/g, replacement: '%2Finsurance%2F:token' },
    { pattern: /(%2[fF](?:sign|onboard|voe-verify|quote)%2[fF])(?:(?!%2[fF]|%3[fF]|%26|%23)[^&#?\s])+/g, replacement: '$1:token' },
    { pattern: /([?&](?:token|resume|nonce)=)[^&#?\s]+/g, replacement: '$1:redacted' },
    { pattern: /((?:%3[fF]|%26)(?:token|resume|nonce)%3[dD])(?:(?!%26|%23|%3[fF])[^&#?\s])+/g, replacement: '$1:redacted' },
  ],
})

// Global JS error capture — must run after Pinia is registered.
installErrorCapture(app)
// Staff-gated nudge — must run AFTER installErrorCapture so it can compose
// with the errorHandler installErrorCapture just set.
installErrorReporter(app)

// Mount
app.mount('#app')
